import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GeideaCheckoutService } from '../../services/geidea-checkout.service';
import { OnlinePaymentService } from '../../services/online-payment.service';
import { PaymentEventsService } from '../../services/payment-events.service';
import { DataService } from '../../services/data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { GeideaCallbackData, MastercardPayload } from '../../models/payment.model';
import { PayAtTableNiPayload } from '../../models/pay-at-table.model';

@Component({
  selector: 'app-geidea',
  templateUrl: './geidea.component.html',
  styleUrls: ['./geidea.component.scss'],
  standalone: false
})
export class GeideaComponent implements OnInit, OnDestroy {

  public loading = true;
  public readonly dropinContainerId = 'geidea-dropin-container';

  private payload: MastercardPayload | PayAtTableNiPayload | null = null;
  private chequeId: string | null = null;
  private resultHandled = false;

  // Geidea's checkout.min.js hardcodes a 650px min-height on the iframe
  private readonly dropinIframeMinHeight = 'calc(100dvh - 40px)';
  private iframeHeightObserver: MutationObserver | null = null;

  constructor(
    private geideaCheckoutService: GeideaCheckoutService,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private dataService: DataService,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    const payload = this.onlinePaymentService.consumePendingPayload();

    if (!payload) {
      this.location.back();
      return;
    }

    this.payload = payload;
    this.chequeId = 'cheque_id' in payload ? payload.cheque_id ?? null : null;

    this.startSession();
  }

  public ngOnDestroy(): void {
    this.iframeHeightObserver?.disconnect();
    this.geideaCheckoutService.unmount(this.dropinContainerId);
  }

  // Bound to the top-bar's customBack input, and also called directly by Geidea's
  // own SDK cancel button mid-widget (via onCancel below).
  public cancel = (): void => {
    this.paymentEventsService.notifyPaymentCancelled();
    this.location.back();
  };

  private startSession(): void {
    if (!this.payload) {
      return;
    }

    this.loading = true;
    this.resultHandled = false;
    this.geideaCheckoutService.getSession(this.payload).subscribe({
      next: (session) => {
        this.geideaCheckoutService.mount(this.dropinContainerId, session, {
          onSuccess: (data) => this.handlePaymentResult(session.transaction_id, data),
          onError: () => this.handleDeclinedPayment(),
          onCancel: () => this.cancel(),
        }).then((mounted) => {
          this.loading = false;
          if (!mounted) {
            this.handleFatalError();
            return;
          }
          this.watchIframeHeight();
        });
      },
      error: () => this.handleFatalError(),
    });
  }

  private handlePaymentResult(transactionId: string, data: GeideaCallbackData): void {
    if (this.resultHandled) {
      return;
    }
    this.resultHandled = true;

    if (!data?.orderId) {
      this.handleFatalError();
      return;
    }

    this.loading = true;

    this.geideaCheckoutService.validateGeideaSession({
      transaction_id: transactionId,
      order_id: data.orderId,
      ...(this.chequeId ? { cheque_id: this.chequeId } : {}),
    }).subscribe((validated) => {
      if (!validated) {
        this.handleFatalError();
        return;
      }
      this.dataService.transactionLoaded.set(false);
      this.dialog.closeAll();
      this.router.navigate(['/bill-paid'], {
        queryParams: { table_id: this.dataService.table()?.id, transaction_id: transactionId },
      });
    });
  }

  private watchIframeHeight(): void {
    const container = document.getElementById(this.dropinContainerId);
    if (!container) {
      return;
    }

    this.iframeHeightObserver?.disconnect();
    this.enforceIframeHeight(container);

    this.iframeHeightObserver = new MutationObserver(() => this.enforceIframeHeight(container));
    this.iframeHeightObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  private enforceIframeHeight(container: HTMLElement): void {
    const iframe = container.querySelector('iframe');
    if (!iframe || iframe.style.getPropertyValue('min-height') === this.dropinIframeMinHeight) {
      return;
    }
    iframe.style.setProperty('min-height', this.dropinIframeMinHeight, 'important');
    iframe.style.setProperty('height', this.dropinIframeMinHeight, 'important');
  }

  private handleDeclinedPayment(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.geideaCheckoutService.unmount(this.dropinContainerId);
    this.startSession();
  }

  private handleFatalError(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.geideaCheckoutService.unmount(this.dropinContainerId);
    this.location.back();
  }

}
